import { CheckIcon, ChevronDownIcon } from "@heroicons/react/outline";
import { useMemo } from "react";

import { CATEGORY_LABEL, getEventTypesGroupedByCategory } from "./eventTaxonomy";

import Checkbox from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface Props {
  selectedValues: string[];
  onValueChange: (values: string[]) => void;
  placeholder?: string;
}

/** Multi-select event type picker that portals its menu (works inside filter popovers). */
export const AuditEventTypeSelect = ({
  selectedValues,
  onValueChange,
  placeholder = "All event types",
}: Props) => {
  const groupedEventTypes = useMemo(() => getEventTypesGroupedByCategory(), []);
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const displayLabel = useMemo(() => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      for (const group of groupedEventTypes) {
        const match = group.eventTypes.find(opt => opt.value === selectedValues[0]);
        if (match) {
          return match.label;
        }
      }
      return selectedValues[0];
    }
    return `${selectedValues.length} selected`;
  }, [groupedEventTypes, placeholder, selectedValues]);

  const toggleValue = (value: string) => {
    const next = new Set(selectedSet);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onValueChange(Array.from(next));
  };

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            selectedValues.length === 0 && "text-muted-foreground",
          )}
          onPointerDown={event => event.stopPropagation()}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={event => event.preventDefault()}
        onWheel={event => event.stopPropagation()}
      >
        <div className="max-h-60 overflow-y-auto overscroll-contain py-1">
          {groupedEventTypes.map(group => (
            <div key={group.category} className="border-b border-gray-100 p-2 last:border-b-0">
              <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {CATEGORY_LABEL[group.category]}
              </div>
              {group.eventTypes.map(opt => {
                const checked = selectedSet.has(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleValue(opt.value)}
                    className="flex w-full items-start gap-2 rounded px-1 py-1.5 text-left text-sm hover:bg-gray-50"
                  >
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                      <Checkbox checked={checked} className="pointer-events-none h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-gray-900">{opt.label}</span>
                      <span className="block font-mono text-[10px] text-gray-500">{opt.value}</span>
                    </span>
                    {checked ? (
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
